/**
 * HeroEventWall — the drifting two-column wall of event cards behind the hero,
 * ported from the landing page so the web app opens on the same image.
 *
 * Unlike the landing page, which drifts a hardcoded list, this shows real
 * events from the API. It is decoration, not navigation: the whole wall is
 * aria-hidden and pointer-events: none, so nothing here is clickable or
 * focusable and screen readers skip it. The events are all reachable through
 * the rails below, which is where the real links live.
 */
import React, { useMemo } from 'react';
import { getEventCategory } from '../../theme/categories';

// Below this the wall doesn't have the height to read as motion, and the
// duplicated column would just be a long scroll on a phone.
const MIN_EVENTS = 4;

function WallCard({ event }) {
  const cat = getEventCategory(event.category);
  const date = event.date ? new Date(event.date) : null;
  const day = date ? String(date.getDate()).padStart(2, '0') : '';
  const month = date ? date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : '';
  const city = event.venue?.city ? event.venue.city.toUpperCase() : '';
  const kicker = [(event.category || 'EVENT').toUpperCase(), city].filter(Boolean).join(' · ');

  return (
    <div className="vb-wall__card">
      {event.coverImage ? (
        <img
          src={event.coverImage}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(120% 120% at 22% 14%, ${cat.color}44, transparent 56%), linear-gradient(150deg, #1E1A14 0%, #0A0908 100%)`,
        }} />
      )}
      <div className="vb-wall__scrim" />
      {date && (
        <div className="vb-wall__stamp">
          <div className="vb-wall__day">{day}</div>
          <div className="vb-wall__month">{month}</div>
        </div>
      )}
      <div className="vb-wall__meta">
        {kicker && <div className="vb-wall__kicker">{kicker}</div>}
        <h4 className="vb-wall__title">{event.title}</h4>
      </div>
    </div>
  );
}

export default function HeroEventWall({ events }) {
  // Split into two columns that drift against each other, then double each one
  // so the -50% keyframe lands exactly on a repeat and the loop never jumps.
  const [colA, colB] = useMemo(() => {
    const list = (events || []).filter(ev => ev && ev.title);
    if (list.length < MIN_EVENTS) return [[], []];
    const a = list.filter((_, i) => i % 2 === 0);
    const b = list.filter((_, i) => i % 2 === 1);
    return [a.concat(a), b.concat(b)];
  }, [events]);

  if (!colA.length || !colB.length) return null;

  return (
    <div className="vb-wall" aria-hidden="true">
      <div className="vb-wall__col">
        {colA.map((ev, i) => <WallCard key={`a-${ev._id || ev.slug}-${i}`} event={ev} />)}
      </div>
      <div className="vb-wall__col vb-wall__col--b">
        {colB.map((ev, i) => <WallCard key={`b-${ev._id || ev.slug}-${i}`} event={ev} />)}
      </div>
    </div>
  );
}
